/**
 * Warehouse list parse boundary (WSRADM-0013).
 *
 * A bad row is dropped (`null`); a malformed envelope (missing `content` array)
 * throws `ApiError` `UNKNOWN` so `useResource` is `error` — fail closed, never a
 * fake empty table. Required identity fields stay `number` (`sourceUserId` /
 * `sourceSaleId`); they are not run through `asId`.
 */
import { parseMembershipList } from '@/api/dimensions/parse';
import { ApiError } from '@/api/errors';
import { asBoolean, asNumber, asText, isObject } from '@/api/parse';
import type {
  AttendancePageResponse,
  FilterMatch,
  NameMatch,
  PagedResponse,
  ProductSaleLine,
  WarehouseAttendance,
  WarehouseProduct,
  WarehouseProductCategory,
  WarehouseProductLabel,
  WarehouseSale,
  WarehouseSaleDetail,
  WarehouseUser,
} from './types';

function parseNameMatch(value: unknown): NameMatch {
  if (value === 'MATCHED' || value === 'AMBIGUOUS' || value === 'UNMATCHED' || value === 'NONE') {
    return value;
  }
  return 'UNMATCHED';
}

function parseFilterMatch(value: unknown): FilterMatch | null {
  if (value === 'EXACT' || value === 'AMBIGUOUS') return value;
  return null;
}

function parseStringList(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  const out: string[] = [];
  for (const item of value) {
    const text = asText(item);
    if (text !== null) out.push(text);
  }
  return out;
}

function parseChildren<T>(value: unknown, parseOne: (raw: unknown) => T | null): T[] {
  if (!Array.isArray(value)) return [];
  const out: T[] = [];
  for (const item of value) {
    const parsed = parseOne(item);
    if (parsed !== null) out.push(parsed);
  }
  return out;
}

export function parseWarehouseUser(raw: unknown): WarehouseUser | null {
  if (!isObject(raw)) return null;
  const sourceUserId = asNumber(raw['sourceUserId']);
  const isActive = asBoolean(raw['isActive']);
  const isEmployee = asBoolean(raw['isEmployee']);
  const isAdmin = asBoolean(raw['isAdmin']);
  const isSuperAdmin = asBoolean(raw['isSuperAdmin']);
  const hasPendingRequiredDocuments = asBoolean(raw['hasPendingRequiredDocuments']);
  const statusIsActive = asBoolean(raw['statusIsActive']);
  const statusIsOnHold = asBoolean(raw['statusIsOnHold']);
  const statusDaysNoVisit = asNumber(raw['statusDaysNoVisit']);
  const sourceSyncedAt = asText(raw['sourceSyncedAt']);
  if (
    sourceUserId === null ||
    isActive === null ||
    isEmployee === null ||
    isAdmin === null ||
    isSuperAdmin === null ||
    hasPendingRequiredDocuments === null ||
    statusIsActive === null ||
    statusIsOnHold === null ||
    statusDaysNoVisit === null ||
    sourceSyncedAt === null
  ) {
    return null;
  }
  return {
    sourceUserId,
    name: asText(raw['name']),
    firstName: asText(raw['firstName']),
    lastName: asText(raw['lastName']),
    email: asText(raw['email']),
    phone: asText(raw['phone']),
    gender: asText(raw['gender']),
    clientId: asText(raw['clientId']),
    isActive,
    isEmployee,
    isAdmin,
    isSuperAdmin,
    hasPendingRequiredDocuments,
    dateCreated: asText(raw['dateCreated']),
    birthDate: asText(raw['birthDate']),
    nextPaymentDateCache: asText(raw['nextPaymentDateCache']),
    statusExpirationDate: asText(raw['statusExpirationDate']),
    statusBlockedDate: asText(raw['statusBlockedDate']),
    statusLastVisit: asText(raw['statusLastVisit']),
    statusIsActive,
    statusIsOnHold,
    statusDaysNoVisit,
    membershipStatus: asText(raw['membershipStatus']),
    memberSegment: asText(raw['memberSegment']),
    renewalDueSoon: asBoolean(raw['renewalDueSoon']),
    memberValueBand: asText(raw['memberValueBand']),
    memberTenureBand: asText(raw['memberTenureBand']),
    categories: parseStringList(raw['categories']),
    dimensionFamilies: parseMembershipList(raw['dimensionFamilies']),
    sourceSyncedAt,
  };
}

export function parseWarehouseSaleDetail(raw: unknown): WarehouseSaleDetail | null {
  if (!isObject(raw)) return null;
  const sourceDetailId = asNumber(raw['sourceDetailId']);
  if (sourceDetailId === null) return null;
  return {
    sourceDetailId,
    productName: asText(raw['productName']),
    payedPrice: asNumber(raw['payedPrice']),
    quantity: asNumber(raw['quantity']),
    lineAmount: asNumber(raw['lineAmount']),
    sourceProductId: asNumber(raw['sourceProductId']),
    productType: asText(raw['productType']),
    originalPrice: asNumber(raw['originalPrice']),
    priceChangeExplanation: asText(raw['priceChangeExplanation']),
    taxes: asNumber(raw['taxes']),
    expirationDate: asText(raw['expirationDate']),
    dateBlocked: asText(raw['dateBlocked']),
    blockReason: asText(raw['blockReason']),
    reservationsCount: asNumber(raw['reservationsCount']),
    pendingVisits: asNumber(raw['pendingVisits']),
    totalPendingVisits: asNumber(raw['totalPendingVisits']),
    isActive: asBoolean(raw['isActive']),
    families: parseMembershipList(raw['families']),
  };
}

export function parseWarehouseSale(raw: unknown): WarehouseSale | null {
  if (!isObject(raw)) return null;
  const sourceSaleId = asNumber(raw['sourceSaleId']);
  const deleted = asBoolean(raw['deleted']);
  const sourceSyncedAt = asText(raw['sourceSyncedAt']);
  if (sourceSaleId === null || deleted === null || sourceSyncedAt === null) return null;
  return {
    sourceSaleId,
    saleDate: asText(raw['saleDate']),
    total: asNumber(raw['total']),
    currency: asText(raw['currency']),
    memberName: asText(raw['memberName']),
    sourceClientId: asNumber(raw['sourceClientId']),
    sourceUserId: asNumber(raw['sourceUserId']),
    sourceCreatedAt: asText(raw['sourceCreatedAt']),
    taxes: asNumber(raw['taxes']),
    exchangeRate: asNumber(raw['exchangeRate']),
    documentNumber: asNumber(raw['documentNumber']),
    paymentAccountName: asText(raw['paymentAccountName']),
    paymentAccountType: asText(raw['paymentAccountType']),
    exchangeCurrency: asText(raw['exchangeCurrency']),
    deleted,
    sourceSyncedAt,
    details: parseChildren(raw['details'], parseWarehouseSaleDetail),
  };
}

export function parseWarehouseAttendance(raw: unknown): WarehouseAttendance | null {
  if (!isObject(raw)) return null;
  const naturalKeyHash = asText(raw['naturalKeyHash']);
  const attendedOn = asText(raw['attendedOn']);
  const sourceSyncedAt = asText(raw['sourceSyncedAt']);
  if (naturalKeyHash === null || attendedOn === null || sourceSyncedAt === null) return null;
  return {
    naturalKeyHash,
    attendedOn,
    attendedAtTime: asText(raw['attendedAtTime']),
    className: asText(raw['className']),
    memberName: asText(raw['memberName']),
    instructor: asText(raw['instructor']),
    productName: asText(raw['productName']),
    sourceClientId: asNumber(raw['sourceClientId']),
    memberMatch: parseNameMatch(raw['memberMatch']),
    sourceProductId: asNumber(raw['sourceProductId']),
    productMatch: parseNameMatch(raw['productMatch']),
    sourceSyncedAt,
  };
}

function parseProductCategory(raw: unknown): WarehouseProductCategory | null {
  if (!isObject(raw)) return null;
  const sourceCategoryId = asNumber(raw['sourceCategoryId']);
  if (sourceCategoryId === null) return null;
  return { sourceCategoryId, name: asText(raw['name']) };
}

function parseProductLabel(raw: unknown): WarehouseProductLabel | null {
  if (!isObject(raw)) return null;
  const sourceLabelId = asNumber(raw['sourceLabelId']);
  if (sourceLabelId === null) return null;
  return {
    sourceLabelId,
    name: asText(raw['name']),
    position: asNumber(raw['position']),
  };
}

export function parseWarehouseProduct(raw: unknown): WarehouseProduct | null {
  if (!isObject(raw)) return null;
  const sourceProductId = asNumber(raw['sourceProductId']);
  const sourceSyncedAt = asText(raw['sourceSyncedAt']);
  if (sourceProductId === null || sourceSyncedAt === null) return null;
  const isCurrent = asBoolean(raw['isCurrent']);
  if (isCurrent === null) return null;
  return {
    sourceProductId,
    name: asText(raw['name']),
    productType: asText(raw['productType']),
    price: asNumber(raw['price']),
    currency: asText(raw['currency']),
    taxRate: asNumber(raw['taxRate']),
    restriction: asText(raw['restriction']),
    isGroup: asBoolean(raw['isGroup']),
    userGroupTotal: asNumber(raw['userGroupTotal']),
    payOnline: asBoolean(raw['payOnline']),
    sourceCreatedAt: asText(raw['sourceCreatedAt']),
    durationUnit: asText(raw['durationUnit']),
    durationQuantity: asNumber(raw['durationQuantity']),
    durationDays: asNumber(raw['durationDays']),
    hasPendingVisits: asBoolean(raw['hasPendingVisits']),
    pendingVisits: asNumber(raw['pendingVisits']),
    isCurrent,
    sourceSystem: asText(raw['sourceSystem']),
    sourceSyncedAt,
    categories: parseChildren(raw['categories'], parseProductCategory),
    labels: parseChildren(raw['labels'], parseProductLabel),
    families: parseMembershipList(raw['families']),
  };
}

function parsePaged<T>(body: unknown, parseRow: (raw: unknown) => T | null): PagedResponse<T> {
  if (!isObject(body) || !Array.isArray(body['content'])) {
    throw new ApiError('UNKNOWN', 0, {});
  }
  const content: T[] = [];
  for (const entry of body['content']) {
    const row = parseRow(entry);
    if (row !== null) content.push(row);
  }
  return {
    content,
    page: asNumber(body['page']) ?? 0,
    size: asNumber(body['size']) ?? 0,
    totalElements: asNumber(body['totalElements']) ?? 0,
    totalPages: asNumber(body['totalPages']) ?? 0,
  };
}

export function parseUsersPage(body: unknown): PagedResponse<WarehouseUser> {
  return parsePaged(body, parseWarehouseUser);
}

export function parseSalesPage(body: unknown): PagedResponse<WarehouseSale> {
  return parsePaged(body, parseWarehouseSale);
}

export function parseAttendancePage(body: unknown): AttendancePageResponse {
  const page = parsePaged(body, parseWarehouseAttendance);
  // parsePaged already requires an object with a content array.
  const raw = body as Record<string, unknown>;
  return {
    ...page,
    memberFilterMatch: parseFilterMatch(raw['memberFilterMatch']),
    memberFilterSharedWith: asNumber(raw['memberFilterSharedWith']),
    productFilterMatch: parseFilterMatch(raw['productFilterMatch']),
    productFilterSharedWith: asNumber(raw['productFilterSharedWith']),
  };
}

export function parseProductsPage(body: unknown): PagedResponse<WarehouseProduct> {
  return parsePaged(body, parseWarehouseProduct);
}

export function parseProductSaleLine(raw: unknown): ProductSaleLine | null {
  if (!isObject(raw)) return null;
  const sourceSaleId = asNumber(raw['sourceSaleId']);
  const sourceDetailId = asNumber(raw['sourceDetailId']);
  const deleted = asBoolean(raw['deleted']);
  if (sourceSaleId === null || sourceDetailId === null || deleted === null) return null;
  return {
    sourceSaleId,
    sourceDetailId,
    saleDate: asText(raw['saleDate']),
    sourceClientId: asNumber(raw['sourceClientId']),
    memberName: asText(raw['memberName']),
    quantity: asNumber(raw['quantity']),
    payedPrice: asNumber(raw['payedPrice']),
    lineAmount: asNumber(raw['lineAmount']),
    currency: asText(raw['currency']),
    documentNumber: asNumber(raw['documentNumber']),
    paymentAccountName: asText(raw['paymentAccountName']),
    deleted,
  };
}

export function parseProductSalesPage(body: unknown): PagedResponse<ProductSaleLine> {
  return parsePaged(body, parseProductSaleLine);
}
